import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import debounce from '@helpers/debounce';
import useCustomer from '@hooks/useCustomer';
import paths from '@constants/paths';
import KEYS from '@helpers/keys';
import Link from '@atoms/Link/Link';
import Text from '@atoms/Text/Text';
import { trackSearchSuggestions } from '@helpers/analyticsHelpers/trackDeliveryWidget';
import Config from '@config';
import logger from '@logger';
import PickupLocationListItem from '@molecules/Autocomplete/ListItems/PickupLocationListItem';
import { AxfoodStoreInfoViewModel } from '@occ/api-client';
import {
  StyledAutoComplete,
  StyledAutoCompleteContainer,
  StyledEmptySlot,
  StyledEmptySlotContainer,
  StyledEmptySlotHead,
  StyledSpinner,
} from './PickUpInStoreSearch.styles';
import useStore from '@hooks/useStore';
import InputField from '@atoms/InputField/InputField';
import useCustomRouter from '@hooks/useCustomRouter';
import useCart from '@hooks/useCart';
import { getSuggestedPickupStore, searchStores } from '@api/interfaces/storeApi';

type addressType = {
  town: string;
  line1: string;
  postalCode: string;
  name: string;
};

export interface PickupInStoreSearchProps {
  title: string;
  finishPickUpSubmission: (id: string, callback: () => void) => Promise<any>;
  onCancel?: () => void;
}

const PickupInStoreSearch = ({ title, finishPickUpSubmission, onCancel }: PickupInStoreSearchProps) => {
  const { customer } = useCustomer();
  const { store } = useStore();
  const { cart } = useCart();
  const isOrderReopened = Boolean(cart?.orderReference);
  const MIN_SEARCH_LENGTH = 2;
  const [pickupLocationInputValue, setPickupLocationInputValue] = useState<string>('');
  const [suggestionForYou, setSuggestionForYou] = useState(null);
  const [searchSuggestions, setSearchSuggestions] = useState<AxfoodStoreInfoViewModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation('deliverypicker');
  const router = useCustomRouter();
  const isCheckout = useMemo(() => router.pathname === paths.CHECKOUT, [router.pathname]);
  const autoCompleteRef = useRef<HTMLDivElement>(null);

  const getPickupLocationSuggestions = (searchString: string) => {
    setIsLoading(true);
    searchStores(
      encodeURIComponent(searchString.toLowerCase()),
      'score-desc',
      undefined,
      undefined,
      customer?.isB2BCustomer,
      true
    )
      .then((res) => {
        setSearchSuggestions(res.results);
        setIsLoading(false);
        trackSearchSuggestions(false, res.results, isCheckout);
      })
      .catch((e) => {
        logger.error({ error: 'Could not fetch pickup locations.', message: e.message });
        setIsLoading(false);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetPickupLocationSuggestions = useCallback(
    debounce(getPickupLocationSuggestions, Config.TIMEOUT.addressSearchGetSuggestionsMs),
    []
  );
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSuggestionForYou(null);

    const { value } = e.currentTarget;
    let shouldFetchSuggestions = false;

    if (value.length >= MIN_SEARCH_LENGTH) {
      shouldFetchSuggestions = true;
    } else {
      setSearchSuggestions([]);
    }

    setPickupLocationInputValue(value);
    if (shouldFetchSuggestions) {
      debouncedGetPickupLocationSuggestions(value);
    } else {
      debouncedGetPickupLocationSuggestions.cancel();
    }
  };

  const submitPickUp = async (inputValue: string, pickUpLocationId: string) => {
    if (!inputValue || !pickUpLocationId) return;
    setIsLoading(true);
    await finishPickUpSubmission(pickUpLocationId, () => setIsLoading(false));
  };

  const onClearHandler = () => {
    setPickupLocationInputValue('');
    setSearchSuggestions([]);
  };

  const pickupLocationListItemOnClick = async (name: string, pickupLocationId: string, address: addressType) => {
    setPickupLocationInputValue(name);
    await submitPickUp(name, pickupLocationId);
  };

  const setInitialPickupLocation = async () => {
    let initialStore: AxfoodStoreInfoViewModel | undefined = store;
    if (cart?.deliveryModeCode === Config.HOME_DELIVERY || !cart?.deliveryModeCode || !store?.activelySelected) {
      try {
        const { data: suggestedStore } = await getSuggestedPickupStore(false);
        initialStore = suggestedStore;
      } catch (e) {
        if (e instanceof Error) {
          logger.error({ error: 'Could not fetch suggested store ', message: e?.message });
        }
        initialStore = undefined;
      }
    }

    if (initialStore) {
      if (initialStore.activelySelected && !initialStore.external) {
        setSuggestionForYou({ ...initialStore, displayName: initialStore.name } as any);
      }
    }
  };

  useEffect(() => {
    setInitialPickupLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emptyAutocompleteFragment = !searchSuggestions && pickupLocationInputValue.length >= MIN_SEARCH_LENGTH && (
    <StyledEmptySlot>
      <StyledEmptySlotHead>
        {t('deliveryPicker->autoComplete->pickupLocation->emptySlot->head', {
          search: pickupLocationInputValue,
        })}
      </StyledEmptySlotHead>

      <StyledEmptySlotContainer>
        <Text>{t('deliveryPicker->autoComplete->pickupLocation->emptySlot->text')}</Text>
        <Text>{t('deliveryPicker->autoComplete->pickupLocation->emptySlot->notfound')}</Text>
      </StyledEmptySlotContainer>
      <Link href={paths.PICKUP_STORES} target="_blank">
        {t('deliveryPicker->autoComplete->pickupLocation->emptySlot->link')}
      </Link>
    </StyledEmptySlot>
  );

  const autocompleteFragment = (
    <StyledAutoComplete
      errorMessage={''}
      searchTerm={pickupLocationInputValue}
      suggestions={searchSuggestions}
      ListComponent={PickupLocationListItem}
      listComponentOnClick={pickupLocationListItemOnClick}
      emptySlot={emptyAutocompleteFragment}
      showAsCards
      isOrderReopened={isOrderReopened}
      ref={autoCompleteRef}
    />
  );
  const suggestionForYouFragment = (
    <>
      <Text type="subtitle">{t('deliveryPicker->autoComplete->suggestionForYou')}</Text>
      <StyledAutoComplete
        errorMessage={''}
        searchTerm=""
        suggestions={[suggestionForYou]}
        isOrderReopened={isOrderReopened}
        ListComponent={PickupLocationListItem}
        listComponentOnClick={pickupLocationListItemOnClick}
        emptySlot=""
        showAsCards
      />
    </>
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KEYS.TAB:
        e.currentTarget.blur();
        break;
      case KEYS.UP:
        e.preventDefault();
        break;
      case KEYS.DOWN:
        e.preventDefault();
        autoCompleteRef.current?.querySelector<HTMLOptionElement>('[role="option"]')?.focus();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Text type="body" size="medium">
        {title}
      </Text>
      <InputField
        type="search"
        name="searchStore"
        aria-label={t('deliveryPicker->placeholder->store')}
        placeholder={t('deliveryPicker->placeholder->store')}
        value={pickupLocationInputValue}
        onChange={onChange}
        onClear={onClearHandler}
        onKeyDown={onKeyDown}
        clearable
        showPrependIcon
        autoComplete="off"
        autoFocus
        roundedCorners
      />
      <StyledAutoCompleteContainer>
        {!isLoading && suggestionForYou && suggestionForYouFragment}
        {!isLoading && autocompleteFragment}
        {isLoading && <StyledSpinner size={32} color="black" />}
      </StyledAutoCompleteContainer>
    </>
  );
};

export default PickupInStoreSearch;
